'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { TDynamicData } from '../../types/dynamic-data';

import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { IoStarSharp } from 'react-icons/io5';

export function ReviewsCarousel({ reviews }: { reviews: TDynamicData[] }) {
  const speed = 80;
  const trackRef = useRef<HTMLDivElement>(null);
  const controls = useAnimationControls();
  const [trackWidth, setTrackWidth] = useState(0);
  const [isReducedMotion, setReducedMotion] = useState(false);

  const duplicated = [...reviews, ...reviews, ...reviews];

  const measure = useCallback(() => {
    if (!trackRef.current) return;
    const third = trackRef.current.scrollWidth / 3;
    setTrackWidth(third);
  }, []);

  useEffect(() => {
    measure();
    const handler = () => measure();
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [measure]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(media.matches);

    const listener = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);

  useEffect(() => {
    if (!trackWidth || isReducedMotion) return;

    const duration = speed;

    controls.set({ x: 0 });
    controls.start({
      x: -trackWidth,
      transition: {
        duration,
        ease: 'linear',
        repeat: Infinity,
        repeatType: 'loop',
      },
    });
  }, [trackWidth, speed, controls, isReducedMotion]);

  // Insert MiddleCard in the middle of the array
  const renderCards = [...duplicated];
  const middleIndex = Math.floor(renderCards.length / 2);
  renderCards.splice(middleIndex, 0, { middleCard: true } as any);

  return (
    <section className={cn('ea-section__xl-y', 'relative w-full overflow-hidden')}>
      <motion.div
        ref={trackRef}
        className="flex"
        style={{ willChange: 'transform' }}
        animate={controls}
      >
        {renderCards.map((review, idx) =>
          'middleCard' in review ? (
            <div
              key={`middle-${idx}`}
              className="mx-4 flex-shrink-0"
              style={{ width: 'fit-content' }}
            >
              <MiddleCard />
            </div>
          ) : (
            <div
              key={`${review.name}-${idx}`}
              className="mx-4 flex-shrink-0"
              style={{ width: 'fit-content' }}
            >
              <ReviewCard review={review} />
            </div>
          ),
        )}
      </motion.div>
    </section>
  );
}

function ReviewCard({ review }: { review: TDynamicData }) {
  return (
    <div
      className={cn(
        'flex h-full w-[300px] flex-col items-start justify-between gap-6 rounded-[8px] bg-white p-4 sm:w-[360px] sm:p-5 md:w-[400px] lg:w-[427px] lg:p-6',
        'ea-review-card',
      )}
    >
      <div className="flex w-full flex-col items-start justify-start gap-4">
        <Heading tag="h6" title={review.name} />
        <Paragraph
          color="muted-foreground"
          innerHtml={review.fields[0]?.value}
          className="line-clamp-3 w-full"
        />
      </div>
      <div className="flex w-full flex-col items-start gap-1">
        <div className="flex items-center justify-start gap-1">
          {[...Array(5)].map((_, i) => (
            <IoStarSharp key={i} className="text-[#EDB000]" size={20} />
          ))}
        </div>
        <p className="text-sm font-bold uppercase text-muted-foreground sm:text-base">
          {review.fields[1]?.value}
        </p>
      </div>
    </div>
  );
}

function MiddleCard() {
  return (
    <div
      className={cn(
        'flex h-full w-[300px] flex-col items-start justify-between gap-6 rounded-[8px] border border-border p-4 sm:w-[360px] sm:p-5 md:w-[400px] lg:w-[427px] lg:p-6',
        'ea-review-middle-card',
      )}
    >
      <div className="flex w-full flex-col items-center justify-center gap-2">
        <p className={cn('ea-heading--h3', 'font-semibold tracking-tighter text-black')}>4.9</p>
        <div className="flex items-center justify-start gap-1">
          {[...Array(5)].map((_, i) => (
            <IoStarSharp key={i} className="text-[#EDB000]" size={24} />
          ))}
        </div>
        <Paragraph size="sm" color="muted-foreground">
          Vyše 1000+ hodnotení
        </Paragraph>
      </div>
      <div className="flex w-full items-center justify-center">
        {[...Array(5)].map((_, index) => (
          <Image
            src={`/images/avatars/${index + 1}.jpg`}
            alt={`Avatar ${index + 1}`}
            key={index}
            width={0}
            height={0}
            className={cn(
              'max-h-12 min-h-12 min-w-12 max-w-12 rounded-full border-[2px] border-white object-cover object-center sm:max-h-14 sm:min-h-14 sm:min-w-14 sm:max-w-14',
              index !== 0 && '-ml-4 sm:-ml-6',
            )}
          />
        ))}
      </div>
    </div>
  );
}
